import type { BadgeVariant } from '../../components/ui/Badge';
import type { KhoaDich } from '../../i18n';
import type { LoiChunk, LoiHieuLuc, LoiMaKho, LoiTep, ThaoTacTaiLieu } from './nghiep-vu/tri-thuc';
import type { LoaiTep, TrangThaiMuc, TrangThaiNap, TrangThaiTaiLieu } from './types';

type BienTheTrangThai = Extract<BadgeVariant, 'neutral' | 'primary' | 'success' | 'warning' | 'danger'>;

/**
 * Trạng thái nạp tệp sang tìm kiếm của trợ lý — nhóm biến thể **trạng thái** của `Badge`:
 *
 * - DA_INDEX xanh: trợ lý đã dùng được tệp.
 * - CHO_INDEX, DANG_INDEX xanh nhấn: đang trong tiến trình, không cần ai làm gì.
 * - LOI đỏ: cần người dùng để mắt — bấm "Đồng bộ lại" hoặc xem lỗi.
 * - CHUA_INDEX xám: tệp chỉ có thông tin, hoặc chưa thuộc bộ nào; không phải sự cố.
 */
export const TRANG_THAI_NAP: Record<TrangThaiNap, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  CHUA_INDEX: { khoa: 'triThuc.nap.CHUA_INDEX', variant: 'neutral' },
  CHO_INDEX: { khoa: 'triThuc.nap.CHO_INDEX', variant: 'primary' },
  DANG_INDEX: { khoa: 'triThuc.nap.DANG_INDEX', variant: 'primary' },
  DA_INDEX: { khoa: 'triThuc.nap.DA_INDEX', variant: 'success' },
  LOI: { khoa: 'triThuc.nap.LOI', variant: 'danger' },
};

/** Tệp kẹt trong hàng đợi quá 10 phút: chưa hỏng nhưng người dùng nên để mắt. */
export const BADGE_KET: { khoa: KhoaDich; variant: Extract<BadgeVariant, 'warning'> } = {
  khoa: 'triThuc.nap.ket',
  variant: 'warning',
};

/**
 * Bộ chưa tạo được collection ở index (`collection_error`): tệp tải lên sẽ không nạp được cho tới khi
 * module tạo lại được. Hổ phách vì cần người vận hành xem, nhưng dữ liệu của bộ chưa hỏng gì.
 */
export const BADGE_CHUA_NOI_INDEX: { khoa: KhoaDich; variant: Extract<BadgeVariant, 'warning'> } = {
  khoa: 'triThuc.kho.chuaNoiIndex',
  variant: 'warning',
};

/**
 * Trạng thái tài liệu kiểm duyệt — nhóm biến thể **trạng thái**:
 *
 * - REVIEW hổ phách: đang chờ một người (người duyệt) làm việc.
 * - APPROVED xanh: đã vào kho, trợ lý dùng được.
 * - DRAFT, ARCHIVED xám: chưa dùng hoặc thôi dùng — chủ động, không phải sự cố.
 */
export const TRANG_THAI_TAI_LIEU: Record<TrangThaiTaiLieu, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  DRAFT: { khoa: 'triThuc.trangThaiTaiLieu.DRAFT', variant: 'neutral' },
  REVIEW: { khoa: 'triThuc.trangThaiTaiLieu.REVIEW', variant: 'warning' },
  APPROVED: { khoa: 'triThuc.trangThaiTaiLieu.APPROVED', variant: 'success' },
  ARCHIVED: { khoa: 'triThuc.trangThaiTaiLieu.ARCHIVED', variant: 'neutral' },
};

export const THU_TU_TRANG_THAI_TAI_LIEU: TrangThaiTaiLieu[] = ['DRAFT', 'REVIEW', 'APPROVED', 'ARCHIVED'];

/**
 * Tệp ngoài khoảng hiệu lực. Bộ có cờ `an_tai_lieu_ngoai_hieu_luc` thì trợ lý không dùng tệp này nữa:
 * hết hiệu lực đỏ vì người phụ trách nên thay bản mới; chưa tới hiệu lực hổ phách vì chỉ là chờ ngày.
 */
export const BADGE_HIEU_LUC: Record<'het' | 'chuaToi', { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  het: { khoa: 'triThuc.tep.hetHieuLuc', variant: 'danger' },
  chuaToi: { khoa: 'triThuc.tep.chuaToiHieuLuc', variant: 'warning' },
};

/**
 * Trạng thái một mục trong hàng đợi tải tài liệu (chỉ sống trong trang): chờ xám, đang gửi xanh nhấn,
 * xong xanh, lỗi đỏ.
 */
export const TRANG_THAI_MUC: Record<TrangThaiMuc, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  cho: { khoa: 'triThuc.trangThaiMuc.cho', variant: 'neutral' },
  dang: { khoa: 'triThuc.trangThaiMuc.dang', variant: 'primary' },
  xong: { khoa: 'triThuc.trangThaiMuc.xong', variant: 'success' },
  loi: { khoa: 'triThuc.trangThaiMuc.loi', variant: 'danger' },
};

/** Loại tệp hiện bằng chữ, không tô màu: sáu loại mà nhóm phân loại của `Badge` chỉ có bốn tông. */
export const LOAI_TEP: Record<LoaiTep, KhoaDich> = {
  WORD: 'triThuc.loaiTep.WORD',
  EXCEL: 'triThuc.loaiTep.EXCEL',
  PDF: 'triThuc.loaiTep.PDF',
  IMAGE: 'triThuc.loaiTep.IMAGE',
  POWERPOINT: 'triThuc.loaiTep.POWERPOINT',
  TEXT: 'triThuc.loaiTep.TEXT',
};

export const THU_TU_LOAI_TEP: LoaiTep[] = ['WORD', 'EXCEL', 'PDF', 'IMAGE', 'POWERPOINT', 'TEXT'];

/**
 * Mã quyền module mà từng nút cần — chép đúng `@RequirePermission` ở các controller của
 * `modules/tri-thuc/backend`. Không đủ quyền thì ẩn nút (ADR-UI-0005); server vẫn là chốt cuối.
 */
export const QUYEN = {
  khoTao: 'tri-thuc:kho:create',
  khoSua: 'tri-thuc:kho:update',
  khoXoa: 'tri-thuc:kho:delete',
  thuMucDoc: 'tri-thuc:thu-muc:read',
  thuMucTao: 'tri-thuc:thu-muc:create',
  thuMucSua: 'tri-thuc:thu-muc:update',
  thuMucXoa: 'tri-thuc:thu-muc:delete',
  thuMucChiaSe: 'tri-thuc:thu-muc:share',
  tepDoc: 'tri-thuc:tep:read',
  tepTao: 'tri-thuc:tep:create',
  tepSua: 'tri-thuc:tep:update',
  tepXoa: 'tri-thuc:tep:delete',
  tepChiaSe: 'tri-thuc:tep:share',
  taiLieuTao: 'tri-thuc:tai-lieu:create',
  taiLieuSua: 'tri-thuc:tai-lieu:update',
  taiLieuDuyet: 'tri-thuc:tai-lieu:approve',
  taiLieuLuuTru: 'tri-thuc:tai-lieu:archive',
} as const;

/** Quyền của từng thao tác tài liệu: quay lại phiên bản trước đi cùng quyền lưu trữ, như backend. */
export const QUYEN_THAO_TAC_TAI_LIEU: Record<ThaoTacTaiLieu, keyof typeof QUYEN> = {
  'submit-review': 'taiLieuSua',
  approve: 'taiLieuDuyet',
  'return-draft': 'taiLieuDuyet',
  'request-revision': 'taiLieuDuyet',
  archive: 'taiLieuLuuTru',
  rollback: 'taiLieuLuuTru',
};

/** Số dòng mỗi lần tải (`limit`). */
export const MOT_TRANG = 20;

/** Số tệp mỗi trang ở màn chi tiết bộ: cây thư mục cần thấy nhiều tệp một lúc. */
export const TRANG_TEP = 100;

export const KHOA_LOI_TEP: Record<LoiTep, KhoaDich> = {
  doc: 'triThuc.loi.tepDoc',
  khongNhan: 'triThuc.loi.tepKhongNhan',
  quaLon: 'triThuc.loi.tepQuaLon',
  rong: 'triThuc.loi.tepRong',
};

export const KHOA_LOI_MA_KHO: Record<LoiMaKho, KhoaDich> = {
  trong: 'triThuc.kho.loiMaTrong',
  dai: 'triThuc.kho.loiMaDai',
  sai: 'triThuc.kho.loiMaSai',
};

export const KHOA_LOI_CHUNK: Record<LoiChunk, KhoaDich> = {
  kichThuocThieu: 'triThuc.kho.loiKichThuocThieu',
  kichThuocNgoai: 'triThuc.kho.loiKichThuocNgoai',
  chongLanThieu: 'triThuc.kho.loiChongLanThieu',
  chongLanNgoai: 'triThuc.kho.loiChongLanNgoai',
  chongLanQuaLon: 'triThuc.kho.loiChongLanQuaLon',
};

export const KHOA_LOI_HIEU_LUC: Record<LoiHieuLuc, KhoaDich> = {
  thieuTu: 'triThuc.tep.loiThieuTu',
  denKhongSauTu: 'triThuc.tep.loiDenKhongSauTu',
};

/** Nhãn nút và toast khi xong của từng thao tác tài liệu. */
export const THAO_TAC_TAI_LIEU: Record<ThaoTacTaiLieu, { nhan: KhoaDich; daXong: KhoaDich }> = {
  'submit-review': { nhan: 'triThuc.taiLieu.guiDuyet', daXong: 'triThuc.taiLieu.daGuiDuyet' },
  approve: { nhan: 'triThuc.taiLieu.duyet', daXong: 'triThuc.taiLieu.daDuyet' },
  'return-draft': { nhan: 'triThuc.taiLieu.traNhap', daXong: 'triThuc.taiLieu.daTraNhap' },
  'request-revision': { nhan: 'triThuc.taiLieu.yeuCauSua', daXong: 'triThuc.taiLieu.daYeuCauSua' },
  archive: { nhan: 'triThuc.taiLieu.luuTru', daXong: 'triThuc.taiLieu.daLuuTru' },
  rollback: { nhan: 'triThuc.taiLieu.quayLai', daXong: 'triThuc.taiLieu.daQuayLai' },
};

/** Thao tác cần hộp xác nhận. "Gửi duyệt" bấm là chạy: không mất gì, và quay lại được ngay. */
export type ThaoTacCoHop = Exclude<ThaoTacTaiLieu, 'submit-review'>;

/** Hộp xác nhận: tiêu đề đặt câu hỏi, thân nói hậu quả (`{{ten}}`, `{{bo}}`, `{{trangThai}}` đích). */
export const HOP_THAO_TAC: Record<ThaoTacCoHop, { tieuDe: KhoaDich; than: KhoaDich }> = {
  approve: { tieuDe: 'triThuc.taiLieu.duyetTieuDe', than: 'triThuc.taiLieu.duyetThan' },
  'return-draft': { tieuDe: 'triThuc.taiLieu.traNhapTieuDe', than: 'triThuc.taiLieu.traNhapThan' },
  'request-revision': { tieuDe: 'triThuc.taiLieu.yeuCauSuaTieuDe', than: 'triThuc.taiLieu.yeuCauSuaThan' },
  archive: { tieuDe: 'triThuc.taiLieu.luuTruTieuDe', than: 'triThuc.taiLieu.luuTruThan' },
  rollback: { tieuDe: 'triThuc.taiLieu.quayLaiTieuDe', than: 'triThuc.taiLieu.quayLaiThan' },
};
